import React, { memo } from 'react'
import { useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js'
import { useAppStore } from '../../stores/appStore'
import { normalizeAssetUrl } from '../../config'
import { unifyRepeatsFromBase, applyPBR, loadTextureMaps, swapMeshUV } from './textureUtils'

function AccessoriesCmp({ useUvChecker, rotateQuarterTurns, swapUV, bedLiftY }: { 
  useUvChecker: boolean; 
  rotateQuarterTurns: number; 
  swapUV: boolean; 
  bedLiftY: number 
}) {
  // Select only accessories slice to avoid re-renders from unrelated store updates
  const accessories: any[] = useAppStore((s) => s.bedConfig.accessories) as any[]
  
  const bedBox = (window as any).__bed_bbox__
  if (!bedBox || bedBox.isEmpty()) return null as any

  // Get bed width to determine if we should render both sides or just one
  const selectedSize = useAppStore((s) => s.bedConfig.bed.selectedSize) as number | undefined
  const isSingleBed = selectedSize !== undefined && selectedSize < 150
  // For single beds (<150cm), only render right side (i === 1), no mirroring
  const sidesToRender = isSingleBed ? [1] : [0, 1]

  const BASE_SCALE = 0.05
  const gapFromBed = 0.6

  return (
    <>
      {accessories.map((accessory, accessoryIndex) => {
        if (!accessory?.three_d_model?.file_url) return null

        // Check if this is a tray or pocket - if so, skip it (they are rendered separately in bed group)
        const isTray = accessory.slug?.includes('tray') || accessory.name?.toLowerCase().includes('tray')
        const isPocket = accessory.slug?.includes('pocket') || accessory.name?.toLowerCase().includes('pocket')
        if (isTray || isPocket) return null

        const nameLower = (accessory.name || '').toLowerCase()
        const slugLower = (accessory.slug || '').toLowerCase()
        const isOttoman = slugLower.includes('ottoman') || nameLower.includes('ottoman')
        const isSaturn = slugLower.includes('saturn') || nameLower.includes('saturn')
        const isLamp = slugLower.includes('lamp') || nameLower.includes('lamp')

        const accessoryUrl = normalizeAssetUrl(accessory.three_d_model.file_url)
        const texture = accessory.currentTexture || null
        
        // Load the accessory model
        const accessoryModel = useLoader(FBXLoader, accessoryUrl) as THREE.Group
        const accessoryBox = new THREE.Box3().setFromObject(accessoryModel)
        const accessoryCenter = accessoryBox.getCenter(new THREE.Vector3())
        const accessoryMin = accessoryBox.min
        
        // Other accessories (like lamp) go on bedside tables (duplicated left/right)
        return (
            <React.Fragment key={`accessory-wrapper-${accessory.id}-${accessoryIndex}`}>
              {sidesToRender.map((i) => {
                const accessorySize = new THREE.Vector3().subVectors(accessoryBox.max, accessoryBox.min).multiplyScalar(BASE_SCALE)
                const halfW = accessorySize.x * 0.5
                const isLeft = i === 0
                
                // Position lamps mounted to the side of the bed (same base idea as addon tables)
                let centerX: number
                let centerZ: number
                let accessoryY: number
                
                // Base lamp positioning: mounted to the side of the bed (similar base as addon table),
                // independent of which bedside table is selected.
                const gapFromBed = 0.6
                const customSideGap = -2.5  // Less negative = further outwards from bed (negative moves inwards)
                const customBackOffset = 0
                const customHeightOffset = -0.12  // More negative = lower
                
                centerZ = bedBox.min.z + (accessorySize.z * 0.5) + customBackOffset
                const baseLeftX = bedBox.min.x - gapFromBed - halfW
                const baseRightX = bedBox.max.x + gapFromBed + halfW

                // For lamps:
                // - With two lamps (double bed): swap left/right, which you said looks good.
                // - With a single lamp (single bed: sidesToRender = [1]): render the *other* side
                //   by treating that single instance as if it were the opposite side.
                if (isLamp) {
                  // When single bed and lamp: flip logical side so we use the \"other\" lamp position
                  const lampIsLeftForPlacement = isSingleBed ? !isLeft : isLeft
                  // Swapped mapping: logical \"left\" lamp uses right-side base X, and vice versa
                  centerX = lampIsLeftForPlacement
                    ? (baseRightX + customSideGap)
                    : (baseLeftX - customSideGap)
                } else {
                  // Default behavior for other accessories
                  centerX = isLeft ? (baseLeftX - customSideGap) : (baseRightX + customSideGap)
                }
                accessoryY = bedLiftY + customHeightOffset
              
                const clone = accessoryModel.clone()
                clone.scale.setScalar(BASE_SCALE)
                clone.position.set(-accessoryCenter.x * BASE_SCALE, -accessoryMin.y * BASE_SCALE, -accessoryCenter.z * BASE_SCALE)

                // Apply textures if available
                if (texture?.base_color_url || texture?.base_color_urls) {
                  clone.traverse((child: any) => {
                    if (child.isMesh && child.material) {
                      // Clone geometry so each instance has its own UV data
                      try {
                        child.geometry = (child.geometry as THREE.BufferGeometry).clone()
                        if (child.geometry && child.geometry.userData) {
                          delete child.geometry.userData._origUV
                        }
                      } catch {}
                      swapMeshUV(child, swapUV, `accessory-${accessoryIndex}-${i}`)
                      const mat = new THREE.MeshStandardMaterial()
                      mat.color = new THREE.Color(0xffffff)
                      ;(mat as any).emissive?.setRGB?.(0, 0, 0)
                      ;(mat as any).vertexColors = false
                      const turns: number = Math.abs(rotateQuarterTurns || 0) % 4
                      
                      const loader = new THREE.TextureLoader()
                      const maps = loadTextureMaps(texture, loader)
                      
                      if (maps.base) {
                        maps.base.flipY = false
                        // @ts-ignore
                        if ((THREE as any).SRGBColorSpace) {
                          // @ts-ignore
                          maps.base.colorSpace = (THREE as any).SRGBColorSpace
                        }
                        maps.base.wrapS = maps.base.wrapT = THREE.RepeatWrapping
                        if ((maps.base as any).center && typeof (maps.base as any).center.set === 'function') {
                          ;(maps.base as any).center.set(0.5, 0.5)
                        }
                        maps.base.rotation = turns ? (Math.PI / 2) * turns : 0
                        // Only mirror texture for left side on beds >= 150cm
                        if (isLeft && !isSingleBed) {
                          maps.base.wrapS = THREE.MirroredRepeatWrapping
                          maps.base.repeat.x = 1
                          maps.base.offset.x = 0
                        }
                      }
                      
                      applyPBR({
                        mat,
                        origMat: child.material,
                        type: 'metal',
                        meshName: child.name,
                        maps,
                        rotateTurns: turns,
                        hasUV2: swapUV
                      })
                      child.material = mat
                      unifyRepeatsFromBase(child.material)
                      mat.needsUpdate = true
                    }
                  })
                }

                // Mirror logic:
                // - Original behavior: mirror left side on double beds (isLeft && !isSingleBed).
                // - For a single lamp (single bed), we want to use the *other* lamp,
                //   which in the double-bed case is the left lamp (mirrored). So for lamps on
                //   single beds we always mirror in X.
                const mirrorX =
                  isLamp && isSingleBed
                    ? -1
                    : (isLeft && !isSingleBed ? -1 : 1)

                return (
                  <group key={`accessory-${accessory.id}-${accessoryIndex}-${i}`} position={[centerX, accessoryY, centerZ]}>
                    <group scale={[mirrorX, 1, 1]}>
                      <primitive object={clone} />
                    </group>
                  </group>
                )
              })}
            </React.Fragment>
          )
      })}
    </>
  )
}

const Accessories = memo(AccessoriesCmp)
export default Accessories